import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useToasts } from 'common/toast';

import { Form, Button, Icon, Message } from 'semantic-ui-react';
import { PageContent, PageHeader, PageSection, Subtitle } from 'common/styles/page';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';

import { organizationApi } from 'areas/settings/organization/api/organizationApi';
import { categoryApi } from '../api/categoryApi';
import { CategoryOption } from '../api/categoryApi';

import { SurveystructureCompliance } from 'store/generated/admin/category';
import CategoryOptionModal from './CategoryOptionModal';
import { StyledAddButton } from 'common/components/button/StyledButton';

const CategoryEdit: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);
  const [editingOption, setEditingOption] = useState<CategoryOption | null>(null);

  const [searchParams] = useSearchParams();
  const orgId = searchParams.get('orgId');
  const priority = Number(searchParams.get('priority'));


  const isEdit = Boolean(id);
  const [formState, setFormState] = useState<SurveystructureCompliance>({
    name: '',
    status: 1,
    priority: 1,
    organization: 0,
    categories: [],
    default_prompt: 'never_ask',
    default_visibility: 'private',
    default_question: ''
  });

  const { data: organizations = [] } = organizationApi.useApiAdminV1OrganizationListQuery();
  const organizationOptions = organizations
    .filter(org => !org.is_archived)
    .map(org => ({
      key: org.id,
      text: org.name,
      value: org.id,
    }));

  useEffect(() => {
    if (!isEdit) {
      setFormState(prev => ({
        ...prev,
        ...(orgId ? { organization: Number(orgId) } : {}),
        ...(priority ? { priority: priority as SurveystructureCompliance['priority'] } : {}),
        ...(formState.default_prompt ? { default_prompt: formState.default_prompt } : {}),
        ...(formState.default_visibility ? { default_visibility: formState.default_visibility } : {}),
        ...(formState.default_question ? { default_question: formState.default_question } : {}),
      }));
    }
  }, [isEdit, orgId, priority]);


  const [errors, setErrors] = useState<string | null>(null);

  const { data: loadedCategory, isLoading: isLoadingCategory, error: loadError } =
    categoryApi.useApiAdminV1CategoryRetrieveQuery({ id: Number(id) }, { skip: !isEdit });

  const [createCategory] = categoryApi.useApiAdminV1CategoryCreateMutation();
  const [updateCategory] = categoryApi.useApiAdminV1CategoryPartialUpdateMutation();

  useEffect(() => {
    if (loadedCategory) {
      setFormState({
        name: loadedCategory.name ?? '',
        status: loadedCategory.status ?? 1,
        priority: loadedCategory.priority ?? 1,
        categories: loadedCategory.categories ?? [],
        organization: loadedCategory.organization ?? null,
        default_prompt: loadedCategory.default_prompt ?? 'never_ask',
        default_visibility: loadedCategory.default_visibility ?? 'private',
        default_question: loadedCategory.default_question ?? '',
      });
    }
  }, [loadedCategory]);

  const handleChange = (field: keyof SurveystructureCompliance, value: any) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };


  const handleSaveOption = (option: CategoryOption) => {
    setFormState(prev => {
      const existing = (prev.categories as CategoryOption[]).filter(o => o.id !== option.id);
      return {
        ...prev,
        categories: [...existing, option],
      };
    });
  };



  const handleAddOption = () => {
    setEditingOption(null);
    setIsOptionModalOpen(true);
  };

  const formatCsvValue = (value: string) => {
    const escaped = value.replace(/"/g, '""');
    return `"${escaped}"`;
  };

  const handleCopyOptionsClick = async () => {
    const optionNames = (formState.categories as CategoryOption[]).map(option => option.name || '');
    const csv = optionNames.map(formatCsvValue).join(',');

    try {
      await navigator.clipboard.writeText(csv);
      addToast('Option names copied as CSV', { appearance: 'success' });
    } catch (err) {
      console.error('Failed to copy option names:', err);
      addToast('Unable to copy option names', { appearance: 'error' });
    }
  };

  const handleSubmit = async () => {
    if (!formState.name?.trim()) {
      setErrors('Name is required');
      return;
    }

    setErrors(null);

    try {
      if (isEdit) {
        await updateCategory({
          id: Number(id),
          patchedSurveystructureCompliance: formState,
        }).unwrap();
        addToast('Category updated successfully', { appearance: 'success' });
      } else {
        await createCategory({
          surveystructureCompliance: formState,
        }).unwrap();
        addToast('Category created successfully', { appearance: 'success' });
      }

      navigate(-1);
    } catch (err) {
      console.error('Failed to save category:', err);
      setErrors('Failed to save category');
    }
  };

  const priorityOptions = [
    { key: 1, text: 'Primary', value: 1 },
    { key: 2, text: 'Custom', value: 2 },
  ]

  // Only allow core categories to be created from Categories area, not orgs.
  if (orgId == null) {
    priorityOptions.push({ key: 3, text: 'nuLogic', value: 3 })
  }

  if (isEdit && isLoadingCategory) {
    return (
      <PageContent>
        <PageHeader title="Edit Category" />
        <PageSection>
          <p>Loading category...</p>
        </PageSection>
      </PageContent>
    );
  }

  if (isEdit && loadError) {
    return (
      <PageContent>
        <PageHeader title="Edit Category" />
        <PageSection>
          <Message negative content="Failed to load category." />
          <Button onClick={() => navigate(-1)}>Back to List</Button>
        </PageSection>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <PageHeader title={isEdit ? 'Edit Category' : 'Create Category'}
        controls={
          <>
            <Button onClick={() => navigate(-1)}>Cancel</Button>
            <Button primary onClick={handleSubmit}>
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </>
        }
      />
      <PageSection>
        <Form>
          <Form.Input
            label="Name"
            value={formState.name || ''}
            onChange={(_, { value }) => handleChange('name', value)}
            required
          />
          <Form.Select
            label="Priority"
            options={priorityOptions}
            value={formState.priority}
            onChange={(_, { value }) => {
              if (value === 3) {
                handleChange('organization', null)
              }
              handleChange('priority', value)
            }}
          />

          {!orgId && formState.priority !== 3 && (
            <Form.Select
              label="Organization"
              options={organizationOptions}
              value={formState.organization ?? ''}
              onChange={(_, { value }) => handleChange('organization', Number(value))}
              disabled={!!orgId}
              required
            />
          )}

          <Form.Select
            label="Default Visibility"
            options={[
              { key: 'public', text: 'Public', value: 'public' },
              { key: 'private', text: 'Private', value: 'private' },
            ]}
            value={formState.default_visibility ?? ''}
            onChange={(_, { value }) => handleChange('default_visibility', value)}
          />

          <Form.Select
            label="Default Prompt"
            options={[
              { key: 'never_ask', text: 'Never ask', value: 'never_ask' },
              { key: 'always_ask', text: 'Always ask', value: 'always_ask' },
              { key: 'ask_if_not_found', text: 'Ask if not found', value: 'ask_if_not_found' },
            ]}
            value={formState.default_prompt ?? ''}
            onChange={(_, { value }) => handleChange('default_prompt', value)}
          />

          <Form.Input
            label="Prompt Question"
            value={formState.default_question || ''}
            onChange={(_, { value }) => handleChange('default_question', value)}
          />

          {errors && <Message negative content={errors} />}
        </Form>

        <PageSection padtop display='flex' justifyContent='space-between' alignItems='center'>
          <Subtitle>Options</Subtitle>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Button icon size='mini' onClick={handleCopyOptionsClick} title='Copy option names as CSV' aria-label='Copy option names as CSV'>
              <Icon name='copy' />
            </Button>
            <StyledAddButton onClick={handleAddOption} label='Add Option' />
          </div>
        </PageSection>

        <Table compact>
          <TableHeader>
            <TableRow>
              <HeaderCell>Name</HeaderCell>
              <HeaderCell>Color</HeaderCell>
              <HeaderCell justify="center">Actions</HeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(formState.categories as CategoryOption[]).map((opt, idx) => (
              <TableRow key={opt.id} $selectable onClick={() => {
                setEditingOption(opt);
                setIsOptionModalOpen(true);
              }}>
                <TableCell $firstcol>{opt.name}</TableCell>
                <TableCell>
                  <div style={{ backgroundColor: opt.color, width: 20, height: 20 }} />
                </TableCell>
                <TableCell justify="center">
                  <Button icon="trash" size="mini" onClick={e => {
                    e.stopPropagation();
                    setFormState(prev => ({
                      ...prev,
                      categories: (prev.categories as CategoryOption[]).filter(o => o.id !== opt.id),
                    }));
                  }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <CategoryOptionModal
          open={isOptionModalOpen}
          initialOption={editingOption}
          existingColors={(formState.categories as CategoryOption[]).map(c => c.color)}
          onClose={() => setIsOptionModalOpen(false)}
          onSave={handleSaveOption}
        />

      </PageSection>
    </PageContent>
  );
};

export default CategoryEdit;
